import { apiAddress, apiErrorMessage } from '@/util/http-utils';
import Authorizor from '@/util/authorizor';
import { type Competition, type CompetitionList, type Prize, type Round, type CompetitionStatusRequest, CompetitionStatus } from '@/models/competition';
import { type Person, PersonRole } from '@/models/person';
import { type Jump, type JumpList, JumpStatus } from '@/models/jump';
import { type IdParams } from '@/models/id-params';
import { type IdResponse } from '@/models/id-response';
import { UserRole } from '@/models/profile';
import { type InvitedPerson } from '@/models/invited-person';
import { type BusinessCategory } from '@/models/business-category';
import { type BusinessInfo } from '@/models/business-info';
import ISO8601 from '@/util/iso-dates';
// import  { type TeamMember } from '@/api/model/team-member';

export const getCompetitionWithId = async (competitionId: string): Promise<Competition> => {
    const path = `/competition/${competitionId}`;
    const response = await fetch(apiAddress(path), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    const result = await response.json();
    return result;
};

// this is here only to support development and may be removed at some point
export const getMostRecentDraftJump = async (): Promise<Jump> => {
    const path = `/competition/jump/recent`;
    const response = await fetch(apiAddress(path), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    const result = await response.json();
    return result;
};

export const getAllCompetitions = async (role?: UserRole): Promise<CompetitionList> => {
    const response = await fetch(apiAddress(`/competition/all?role=${role}`), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error("Failed to load competitions");
    }
    return await response.json();
};

export const getCompetitionsElegibleCount = async (): Promise<number> => {
    const competitions = await getAllCompetitions();
    let count = 0;
    let now: Date = ISO8601.toDate(ISO8601.nowUTC());
    competitions.map((competition) => {
        if (calcScheduleEndDate(competition.schedule) > now) {
            count++;
        }
    });
    return count;
};

export const getEligibleCompetitions = async (): Promise<Array<Competition>> => {
    const response = await fetch(apiAddress(`/competition/eligible`), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error("Failed to load competitions");
    }
    const competitions: Competition[] = await response.json();
    const result: Competition[] = [];
    let now: Date = ISO8601.toDate(ISO8601.nowUTC());
    competitions.map((competition) => {
        if (calcScheduleEndDate(competition.schedule) > now) {
            result.push(competition);
        }
    });
    return result;
};

export const getCompetitionWithIdOrganizers = async (competitionId: string): Promise<Array<Person>> => {
    const path = `/competition/${competitionId}/organizers`;
    const response = await fetch(apiAddress(path), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
};

export const getCompetitionWithIdJudges = async (competitionId: string): Promise<Array<Person>> => {
    const path = `/competition/${competitionId}/judges`;
    const response = await fetch(apiAddress(path), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
};

export const setCompetitionWithIdOrganizers = async (competitionId: string, organizers: Array<Person>): Promise<void> => {
    return setPersonsWithRoleForCompetition(competitionId, PersonRole.ORGANIZER, organizers);
}

export const setCompetitionWithIdCompetitors = async (competitionId: string, competitors: Array<Person>): Promise<void> => {
    return setPersonsWithRoleForCompetition(competitionId, PersonRole.COMPETITOR, competitors);
}

export const setCompetitionWithIdJudges = async (competitionId: string, judges: Array<Person>): Promise<void> => {
    return setPersonsWithRoleForCompetition(competitionId, PersonRole.JUDGE, judges);
}

export const deleteCompetitionWithIdOrganizer = async (competitionId: string, email: string): Promise<void> => {
    return deleteOnePersonWithRoleForCompetition(competitionId, PersonRole.ORGANIZER, email);
}

export const deleteCompetitionWithIdCompetitors = async (competitionId: string, email: string): Promise<void> => {
    return deleteOnePersonWithRoleForCompetition(competitionId, PersonRole.COMPETITOR, email);
}

export const deleteCompetitionWithIdJudges = async (competitionId: string, email: string): Promise<void> => {
    return deleteOnePersonWithRoleForCompetition(competitionId, PersonRole.JUDGE, email);
}

export const updateCompetition = async (competition: Competition): Promise<void> => {
    const path = `/competition/${competition.id}`;
    const response: Response = await fetch(apiAddress(path), { 
        method: "PUT",
        headers: Authorizor.headers(),
        body: JSON.stringify(competition)
    });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

export const uploadCompetitionLogo = async (logo: File): Promise<string> => {

    const response = await fetch(apiAddress('/competition/logo'), {
            method: "POST",
            headers: Authorizor.headers({
                'Content-Type': 'application/octet-stream',
                'x-file-type': logo.type
            }),
            body: logo
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return response.text();
}

export const publishCompetition = async (competitionId: string): Promise<void> => {
    const path = `/competition/${competitionId}/status`;
    const statusReq: CompetitionStatusRequest = {
        status: CompetitionStatus.PUBLISHED
    }
    const response: Response = await fetch(apiAddress(path), { 
        method: "PUT",
        headers: Authorizor.headers(),
        body: JSON.stringify(statusReq)
    });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

export const getCompetitionWithIdCompetitors = async (competitionId: string): Promise<Array<Person>> => {
    return getPersonsWithRoleForCompetition(competitionId, PersonRole.COMPETITOR);
};

export const getCompetitionWithIdJumps = async (competitionId: string): Promise<JumpList> => {
    const path = `/competition/${competitionId}/jumps`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
};

export const getCompetitionWithIdAssetFavorite = async (competitionId: string): Promise<IdResponse> => {
    const path = `/competition/${competitionId}/asset/favorite`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

export const setCompetitionWithIdAssetFavorite = async (competitionId: string, favoriteId?: string): Promise<void> => {
    const path = `/competition/${competitionId}/asset/favorite`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            method: "PUT",
            headers: Authorizor.headers(),
            body: JSON.stringify({ id: favoriteId })
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

export const getJumpWithId = async (jumpId: string): Promise<Jump> => {

    const path = `/competition/jump/${jumpId}`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

export const updateJump = async (jump: Jump): Promise<void> => {
    const path = `/competition/jump/${jump.id}`;
    const response: Response = await fetch(apiAddress(path), { 
        method: "PUT",
        headers: Authorizor.headers(),
        body: JSON.stringify(jump)
    });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

export const publishJump = async(jumpId: string, competitionId: string): Promise<void> => {
    const path = `/competition/jump/${jumpId}/publish`;
    const idParams: IdParams = {
        id: competitionId
    }
    const response: Response = await fetch(apiAddress(path), { 
        method: "PUT",
        headers: Authorizor.headers(),
        body: JSON.stringify(idParams)
    });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

export const getJumpWithIdBusinessInfo = async (jumpId: string): Promise<BusinessInfo> => {

    const path = `/competition/jump/${jumpId}/business_info`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

export const getJumpWithIdAssetFavorite = async (jumpId: string): Promise<IdResponse> => {
    const path = `/competition/jump/${jumpId}/asset/favorite`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

export const setJumpWithIdAssetFavorite = async (jumpId: string, favoriteId?: string): Promise<void> => {
    const path = `/competition/jump/${jumpId}/asset/favorite`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            method: "PUT",
            headers: Authorizor.headers(),
            body: JSON.stringify({ id: favoriteId })
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

export const getAllJumps = async (status?: string, role?: UserRole): Promise<JumpList> => {

    const userInArray = (email: string, arr: string[] | undefined) => {
        if (!Array.isArray(arr)) return false;
        for (const testUser of arr) {
            if (testUser?.toLowerCase() === email) {
                return true;
            }
        }
        return false;
    }

    const appendQueryArg = (key: string, value: string, queryString: string) => {
        let result = queryString;
        if (result.length == 0) {
            result = "?";
        }
        else if (result.length > 1) {
            result += "&";
        }
        result += `${key}=${value}`
        return result
    }
    
    let queryString: string = "";
    if (status && status===JumpStatus.DRAFT || JumpStatus.PUBLISHED) {
        queryString = appendQueryArg("status", status!, queryString);
    }
    if (window.location.href.includes('localhost') && role) {
        queryString = appendQueryArg("role", role, queryString);
    }
    let path = `/competition/user/jumps${queryString}`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    const tempJumps = await response.json();
    const result: JumpList = []
    const email = Authorizor.profile?.email.toLowerCase();
    if (email && tempJumps) {
        tempJumps.map((jump: Jump) => {
            if (userInArray(email, jump.reviewers)) {
                jump.userRole = PersonRole.REVIEWER;
            } else if (userInArray(email, jump.team)) {
                jump.userRole = PersonRole.JUMP_ADMIN;
            }
            if (jump.userRole) {
                result.push(jump);
            }
        });
    }
    return result;
}

export const getInvitedGuestInfo = async (magicId: string): Promise<InvitedPerson | undefined> => {
    let path = `/competition/guest/${magicId}`;
    let response: Response | undefined;
    try {
        response = await fetch(apiAddress(path), 
            { 
                headers: Authorizor.headers()
            });
    }
    catch(error) {
        console.error(`ERROR in call to /competition/guest/$magicId: ${error}`);
        return;
    }
    if (response && !response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();

}

export const getTeamMembersForJumpWithId = async (jumpId: string): Promise<Array<Person>> => {
    return await getPersonsWithRoleForJump(jumpId, PersonRole.JUMP_ADMIN);
}

export const getReviewersForJumpWithId = async (jumpId: string): Promise<Array<Person>> => {
    return await getPersonsWithRoleForJump(jumpId, PersonRole.REVIEWER);
}

// export const getVotersForJumpWithId = async (jumpId: string): Promise<Array<Person>> => {
//     return await getPersonsWithRoleForJump(jumpId, PersonRole.VOTER);
// }

export const setTeamMembersForJumpWithId = async (jumpId: string, team: Array<Person>): Promise<void> => {
    return setPersonsWithRoleForJump(jumpId, PersonRole.JUMP_ADMIN, team);
}

export const setOneTeamMemberForJumpWithId = async (jumpId: string, teamMember: Person): Promise<void> => {
    return setOnePersonWithRoleForJump(jumpId, PersonRole.JUMP_ADMIN, teamMember);
}

export const setReviewersForJumpWithId = async (jumpId: string, reviewers: Array<Person>): Promise<void> => {
    return setPersonsWithRoleForJump(jumpId, PersonRole.REVIEWER, reviewers);
}

export const setOneReviewerForJumpWithId = async (jumpId: string, reviewer: Person): Promise<void> => {
    return setOnePersonWithRoleForJump(jumpId, PersonRole.JUMP_ADMIN, reviewer);
}

export const setVotersForJumpWithId = async (jumpId: string, voters: Array<Person>): Promise<void> => {
    return setPersonsWithRoleForJump(jumpId, PersonRole.JUMP_ADMIN, voters);
}

export const setOneVoterForJumpWithId = async (jumpId: string, voter: Person): Promise<void> => {
    return setOnePersonWithRoleForJump(jumpId, PersonRole.JUMP_ADMIN, voter);
}

export const deleteTeamMemberForJumpWithId = async (jumpId: string, email: string) => {
    return deleteOnePersonWithRoleForJump(jumpId, PersonRole.JUMP_ADMIN, email);
}

export const deleteReviewerForJumpWithId = async (jumpId: string, email: string) => {
    return deleteOnePersonWithRoleForJump(jumpId, PersonRole.REVIEWER, email);
}

// export const deleteVoterForJumpWithId = async (jumpId: string, email: string) => {
//     return deleteOnePersonWithRoleForJump(jumpId, PersonRole.VOTER, email);
// }

export const getPrizesForCompetitionWithId = async (competitionId: string): Promise<Array<Prize>> => {
    let path = `/competition/${competitionId}/prizes`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

export const setPrizesForCompetitionWithId = async (competitionId: string, prizes: Prize[]) => {
    const path = `/competition/${competitionId}/prizes`;
    const response: Response = await fetch(apiAddress(path), { 
        method: "PUT",
        headers: Authorizor.headers(),
        body: JSON.stringify(prizes)
    });
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

export const getCategories = async (): Promise<Array<BusinessCategory>> => {
    const path = `/competition/categories`;
    const response = await fetch(apiAddress(path), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

/**
 * Module-private functions
 */

const methodFromCompetitionPersonsRole = (role: PersonRole): string | undefined => {
    let method: string | undefined;
    switch (role) {
        case PersonRole.ORGANIZER:
            method = "organizers";
            break;
        case PersonRole.COMPETITOR:
            method = "competitors";
            break;
        case PersonRole.JUDGE:
            method = "judges"
            break;
    }
    return method;
}

const getPersonsWithRoleForCompetition = async (competitionId: string, role: PersonRole): Promise<Array<Person>> => {
    const method = methodFromCompetitionPersonsRole(role);
    if (!method) {
        throw new Error("Invalid role in getPersonsWithRoleForCompetition");
    }
    const path = `/competition/${competitionId}/${method}`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

const setPersonsWithRoleForCompetition = async (competitionId: string, role: PersonRole, persons: Array<Person>): Promise<void> => {
    const method = methodFromCompetitionPersonsRole(role);
    if (!method) {
        throw new Error("Invalid role in setPersonsWithRoleForJump");
    }
    const path = `/competition/${competitionId}/${method}`;
    const response = await fetch(apiAddress(path), 
        {
            method: "POST",
            headers: Authorizor.headers(),
            body: JSON.stringify(persons)
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

// const setOnePersonWithRoleForCompetition = async (competitionId: string, role: PersonRole, person: Person): Promise<void> => {
//     const method = methodFromJumpPersonsRole(role);
//     if (!method) {
//         throw new Error("Invalid role in setOnePersonWithRoleForJump");
//     }
//     const path = `/competition/${competitionId}/${method}`;
//     const response = await fetch(apiAddress(path), 
//         {
//             method: "POST",
//             headers: Authorizor.headers(),
//             body: JSON.stringify([person])
//         }
//     );
//     if (!response.ok) {
//         throw new Error(apiErrorMessage(response));
//     }
// }

const deleteOnePersonWithRoleForCompetition = async (competitionId: string, role: PersonRole, email: string): Promise<void> => {
    let method: string | undefined;
    switch (role) {
        case PersonRole.ORGANIZER:
            method = "organizer";
            break;
        case PersonRole.COMPETITOR:
            method = "competitor";
            break;
        case PersonRole.JUDGE:
            method = "judge";
            break;
    }
    if (!method) {
        throw new Error("Invalid role in deleteOnePersonWithRoleForCompetition");
    }
    const path = `/competition/${competitionId}/${method}/${email}`;
    const response = await fetch(apiAddress(path), 
        {
            method: "DELETE",
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

const methodFromJumpPersonsRole = (role: PersonRole): string | undefined => {
    let method: string | undefined;
    switch (role) {
        case PersonRole.JUMP_ADMIN:
            method = "team";
            break;
        case PersonRole.REVIEWER:
            method = "reviewers";
            break;
        // case PersonRole.VOTER:
        //     method = "voters"
        //     break;
    }
    return method;
}

const getPersonsWithRoleForJump = async (jumpId: string, role: PersonRole): Promise<Array<Person>> => {
    const method = methodFromJumpPersonsRole(role);
    if (!method) {
        throw new Error("Invalid role in setPersonsWithRoleForJump");
    }
    const path = `/competition/jump/${jumpId}/${method}`;
    const response = await fetch(apiAddress(path), 
        {
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
    return await response.json();
}

const setPersonsWithRoleForJump = async (jumpId: string, role: PersonRole, persons: Array<Person>): Promise<void> => {
    const method = methodFromJumpPersonsRole(role);
    if (!method) {
        throw new Error("Invalid role in setPersonsWithRoleForJump");
    }
    const path = `/competition/jump/${jumpId}/${method}`;
    const response = await fetch(apiAddress(path), 
        {
            method: "POST",
            headers: Authorizor.headers(),
            body: JSON.stringify(persons)
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

const setOnePersonWithRoleForJump = async (jumpId: string, role: PersonRole, person: Person): Promise<void> => {
    const method = methodFromJumpPersonsRole(role);
    if (!method) {
        throw new Error("Invalid role in setOnePersonWithRoleForJump");
    }
    const path = `/competition/jump/${jumpId}/${method}`;
    const response = await fetch(apiAddress(path), 
        {
            method: "POST",
            headers: Authorizor.headers(),
            body: JSON.stringify([person])
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

const deleteOnePersonWithRoleForJump = async (jumpId: string, role: PersonRole, email: string): Promise<void> => {
    let method: string | undefined;
    switch (role) {
        case PersonRole.JUMP_ADMIN:
            method = "member";
            break;
        case PersonRole.REVIEWER:
            method = "reviewer";
            break;
        // case PersonRole.VOTER:
        //     method = "voter";
        //     break;
    }
    if (!method) {
        throw new Error("Invalid role in deleteOnePersonWithRoleForJump");
    }
    const path = `/competition/jump/${jumpId}/${method}/${email}`;
    const response = await fetch(apiAddress(path), 
        {
            method: "DELETE",
            headers: Authorizor.headers()
        }
    );
    if (!response.ok) {
        throw new Error(apiErrorMessage(response));
    }
}

// Calculate the end date of a competition as a javascript date, given a schedule.
const calcScheduleEndDate = (schedule?: Array<Round>): Date => {
    if (!schedule) {
        return ISO8601.toDate(ISO8601.deltaUTC(365));
    }
    let end: Date = ISO8601.toDate(ISO8601.deltaUTC(-10000));
    for (const round of schedule) {
        if (!round.end) continue;
        const roundEnd = ISO8601.toDate(round.end);
        if (roundEnd > end) {
            end = roundEnd;
        }
    }
    return end;
}
